import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getSubjects } from '../../services/content';

const SubjectsList = () => {
  const { getUserLevel, isSuperAdmin } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const levelId = getUserLevel();
        if (!levelId) {
          setError('Unable to determine your education level');
          return;
        }

        // If super admin, fetch subjects for all levels
        let subjectsData;
        if (levelId === 'all') {
          const levels = ['grade7', 'form2', 'grade12'];
          const allSubjects = [];
          for (const level of levels) {
            try {
              const levelSubjects = await getSubjects(level);
              allSubjects.push(...levelSubjects.map(subject => ({ ...subject, level })));
            } catch (levelErr) {
              console.error(`Error fetching subjects for ${level}:`, levelErr);
              // Continue with other levels even if one fails
            }
          }
          subjectsData = allSubjects;
        } else {
          subjectsData = await getSubjects(levelId);
        }
        
        setSubjects(subjectsData);
      } catch (err) {
        setError('Failed to load subjects. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, [getUserLevel, isSuperAdmin]);

  if (loading) {
    return (
      <div className="subjects-list content-page">
        <div className="loading-container">
          <div className="loading-spinner">Loading subjects...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subjects-list content-page">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (subjects.length === 0) {
    return (
      <div className="subjects-list content-page">
        <div className="empty-state">
          <h3>No Subjects Available</h3>
          <p>{isSuperAdmin() ? 'No subjects found in the database. Please add subjects through the admin panel.' : 'Subjects for your level will be available soon.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subjects-list content-page">
      <div className="subjects-header">
        <h2>{isSuperAdmin() ? 'All Subjects (All Levels)' : 'Your Subjects'}</h2>
        <p>{isSuperAdmin() ? 'Access to all subjects across all education levels' : 'Select a subject to access study materials'}</p>
      </div>

      <div className="subjects-grid">
        {subjects.map((subject) => (
          <Link 
            key={`${subject.id}-${subject.level || ''}`} 
            to={`/subjects/${subject.id}${subject.level ? `?level=${subject.level}` : ''}`}
            className="subject-card"
          >
            <div className="subject-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                <polyline points="8,7 16,7 16,11 8,11"/>
                <polyline points="8,13 16,13 16,17 8,17"/>
              </svg>
            </div>
            <div className="subject-info">
              <h3>{subject.name}</h3>
              {subject.level && (
                <span className="subject-level">
                  {subject.level === 'grade7' ? 'Grade 7' : 
                   subject.level === 'form2' ? 'Form 2' : 'Grade 12'}
                </span>
              )}
              <p>{subject.description}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default SubjectsList;
